import React, { useContext } from 'react';
import ModalModule from '../modal';
import Utils from '../utils';
import PlanChangeConfirmationModal from './PlanChangeConfirmationModal.component';
import PlanPriceComparison from './PlanPriceComparison.component';
import UpgradePlanContent from './UpgradePlanContent.component';
import UpgradePlanOrdersAndProductsContent from './UpgradePlanOrdersAndProductsContent.component';
import UpgradePlanUsersContent from './UpgradePlanUsersContent.component';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { BillingPlans } = Utils;
const { prettyPrintPlan } = BillingPlans;

type TriggerFeature = 'Orders' | 'Products' | 'FeeDetails' | 'Users';

interface UpgradePlan {
	Name: string;
	Price: number;
	PlanId: number;
	MaxAllowedUsers?: number;
}

interface UpgradePlanModalData {
	upgradePlan?: UpgradePlan;
	onUpgradePlanCallback?: () => void;
	segmentTrackingCallback?: (action: string) => void;
	triggerFeature?: TriggerFeature;
	upgradePrompt?: string;
}

interface UpgradePlanModalProps {
	modalId: string;
	data: UpgradePlanModalData;
}

const UpgradePlanModal = ({ modalId, data }: UpgradePlanModalProps) => {
	const {
		upgradePlan = {} as UpgradePlan,
		onUpgradePlanCallback,
		segmentTrackingCallback,
		triggerFeature,
		upgradePrompt,
	} = data;
	const { Name, Price, PlanId } = upgradePlan;

	const prettyName = prettyPrintPlan(Name);
	const headers: Record<string, string> = {
		Orders: `Access Stamps.com Orders`,
		Products: `Access Stamps.com Products`,
		FeeDetails: `Change to the ${prettyName} Plan`,
		Users: 'Do You Want to Increase User Seats?',
	};
	const header =
		headers[triggerFeature as string] ?? `Upgrade to the ${prettyName} Plan`;

	const { closeModal, showModal } = useContext(ModalContext);

	const primaryButton = {
		type: ModalButtonActionType.Confirm,
		text: 'Upgrade Now',
		onClick: () => {
			if (segmentTrackingCallback) {
				segmentTrackingCallback('changeNow');
			}

			closeModal(modalId, ModalButtonActionType.Confirm);

			showModal({
				component: PlanChangeConfirmationModal,
				data: {
					newPlanPrice: Price,
					newPlanName: Name,
					newPlanId: PlanId,
					isUpgrade: true,
					upgradePrompt,
					onUpgradePlanCallback,
					triggerFeature,
					previousModalData: data,
				},
			});
		},
		dataqa: 'upgrade-plan-upgrade-now-button',
	};

	const secondaryButton = {
		type: ModalButtonActionType.Cancel,
		text: 'Back',
		buttonType: 'tertiary-ghost',
		onClick: () => {
			if (segmentTrackingCallback) {
				segmentTrackingCallback('dismiss');
			}
			closeModal(modalId, ModalButtonActionType.Cancel);
		},
		dataqa: 'upgrade-plan-back-button',
	};

	const modalSize = ['FeeDetails', 'Orders', 'Products'].includes(
		triggerFeature as string
	)
		? 'medium'
		: 'small';

	const getModalContent = () => {
		switch (triggerFeature) {
			case 'Orders':
			case 'Products':
				return <UpgradePlanOrdersAndProductsContent data={data} />;
			case 'FeeDetails':
				return <PlanPriceComparison data={data} />;
			case 'Users':
				return <UpgradePlanUsersContent data={data} />;
			default:
				return <UpgradePlanContent data={data} />;
		}
	};

	return (
		<div>
			<Modal
				modalId={modalId}
				className={`subscription-plan upgrade-plan-modal ${
					triggerFeature === 'FeeDetails' ? 'fee-details' : ''
				}`}
				header={header}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
				size={modalSize}
			>
				<div className="body">{getModalContent()}</div>
			</Modal>
		</div>
	);
};

export default UpgradePlanModal;
